import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, BUSINESS } from "@/data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollTo(href), open ? 350 : 0);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 lg:px-12 h-[72px]">
          <Logo compact />
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc2 hover:text-goldw transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <a
              href="#contact"
              data-testid="nav-quote-btn"
              onClick={(e) => go(e, "#contact")}
              className="hidden sm:inline-flex items-center border border-goldw/60 text-goldw font-mono text-[11px] tracking-[0.22em] uppercase px-6 py-3 hover:bg-goldw hover:text-ink transition-colors duration-300"
            >
              Get a Free Quote
            </a>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-ivory p-2"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-2xl flex flex-col justify-center px-10 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif text-4xl font-light text-ivory py-3 border-b border-white/5 hover:text-goldw transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.a
              href="#contact"
              data-testid="mobile-quote-btn"
              onClick={(e) => go(e, "#contact")}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 inline-flex justify-center border border-goldw text-goldw font-mono text-xs tracking-[0.25em] uppercase px-8 py-4"
            >
              Get a Free Quote
            </motion.a>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.65 }}
              className="mt-12 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc3"
            >
              {BUSINESS.phone} · {BUSINESS.area}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
